import { MediaContainer } from '@/components/media-container/media-container';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';
import { ExperienceFieldsFragment } from '@/generated/graphql';
import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import ZoomIcon from '../svgs/zoom.svg';
import BookingActions from './booking-actions';

const CardOutline = styled.div`
  display: block;
  width: 100%;
  max-width: 550px;
  background: #ffffff;
  border-radius: 35px;
  padding: 20px;
  box-shadow: 0px 2.76191px 3px 2.76191px rgba(180, 180, 180, 0.25);
  transform: translateY(0);
  transition: transform ease-in-out 0.2s;
  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
    border-radius: unset;
    max-height: unset;
    box-shadow: unset;
    height: 100%;
  }
`;
const ImageSliderWrapper = styled.div`
  width: 100%;
  min-height: 200px;
  aspect-ratio: 4/3;
  background-color: turquoise;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  @media (max-width: 768px) {
    width: 100%;
    border-radius: unset;
    height: 321px;
  }
`;
const ZoomButton = styled.div`
  position: absolute;
  top: 7px;
  right: 7px;
  height: 31px;
  width: 31px;
  z-index: 99;
  pointer-events: none;
  @media (max-width: 768px) {
    display: none;
  }
`;
const ImageContainer = styled(MediaContainer)`
  pointer-events: none;
  width: 100%;
  height: 100%;
  @media (max-width: 768px) {
    height: 321px;
  }
`;

const ImageButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  overflow: hidden;
  display: block;
  width: 100%;
  position: relative;
  cursor: pointer;
  &:focus {
    outline: none;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 159.3%;
  color: #000000;
  margin-top: 24px;
  margin-bottom: 30px;
  text-align: center;
  @media (max-width: 768px) {
    display: none;
  }
`;
const responsive = {
  desktop: {
    breakpoint: { max: 4000, min: 0 },
    items: 1,
  },
};

export interface BookingCardProps {
  experience: ExperienceFieldsFragment;
}
const BookingCard = ({ experience }: BookingCardProps) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const title = experience?.title;
  const images = experience?.images?.edges.map(ed => ed.node.file);

  return (
    <CardOutline>
      <ImageSliderWrapper>
        <Global
          styles={css`
            .react-multi-carousel-item {
              display: flex;
              flex-direction: row;
              justify-content: start;
              a:hover {
                text-decoration: none;
              }
              @media (max-width: 768px) {
                justify-content: start;
              }
            }
            .react-multi-carousel-track {
              margin: 0 !important;
              height: 100%;
              overflow: hidden;
              @media (max-width: 768px) {
                margin: 0 !important;
              }
            }
            .react-multi-carousel-list {
              position: unset;
              height: 100%;
            }
            .react-multi-carousel-dot {
              button {
                background-color: #767676;
                border: none;
              }
            }
            .react-multi-carousel-dot--active {
              button {
                background-color: #ffcf31;
              }
            }
          `}
        />

        <ZoomButton>
          <ZoomIcon />
        </ZoomButton>
        <Carousel
          additionalTransfrom={0}
          centerMode={false}
          swipeable
          draggable
          showDots={true}
          arrows
          responsive={responsive}
          ssr
          keyBoardControl={true}
          transitionDuration={500}
          slidesToSlide={1}
          containerClass="container"
          removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
          deviceType={isMobile ? 'mobile' : 'desktop'}
          sliderClass=""
        >
          {images.map((image, index) => (
            <ImageButton
              data-fancybox="gallery"
              data-src={image?.url}
              data-sizes="(max-width: 600px) 480px, 1000px"
              key={index}
            >
              <ImageContainer>
                {image?.url && (
                  <Image
                    src={image.url}
                    alt="Experience Images"
                    {...nextImageTransitionProps}
                    priority
                  />
                )}
              </ImageContainer>
            </ImageButton>
          ))}
        </Carousel>
      </ImageSliderWrapper>
      <Title>{title}</Title>
      {!isMobile && <BookingActions experience={experience} />}
    </CardOutline>
  );
};

export default BookingCard;
